import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import CustomButton from "../../../../componentLibrary/TopicButton";
import { useGetLesson, useGetTopic } from "../../../../api/queries";
import Spinner from "../../../../componentLibrary/Spinner";
import { useCreateOrGetRecap } from "../../../../api/mutations";
import { useNotification } from "../../../../hooks/useNotification";
import { LessonI, ModuleStatus } from "../../../../types";
import ConfirmationBox from "../../../../componentLibrary/ConfirmationBox";
import { ErrorDisplay } from "../../../../utils/ErrorHandling";

export default function LessonLinkComponent({
  lesson_id,
  module_id,
  page_id,
  className,
  disabled = false,
  label,
  lesson,
  savePageInput,
}: {
  module_id: string;
  lesson_id: string;
  page_id: string;
  className?: string;
  disabled?: boolean;
  label: string;
  lesson?: LessonI;
  savePageInput?: () => void;
}) {
  const [showComponent, setshowComponent] = useState<boolean>(true);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const { moduleId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useNotification();

  const {
    data: lessonLinkDetail,
    isError,
    isLoading,
    error: lessonError,
  } = useGetLesson(lesson_id);
  const {
    data: moduleLinkDetail,
    isError: isErrorModuleLink,
    isLoading: isLoadingModuleLink,
    error: moduleError,
  } = useGetTopic(module_id);
  const { data: currentModuleDetail } = useGetTopic(moduleId);

  const mutation = useCreateOrGetRecap({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["recaps"] });
    },
    onError: async () => {
      notify("error", "There was an error.");
    },
  });

  useEffect(() => {
    if (currentModuleDetail && moduleLinkDetail) {
      if (
        currentModuleDetail.status === ModuleStatus.Published &&
        moduleLinkDetail.status !== ModuleStatus.Published
      ) {
        setshowComponent(false);
      }
    }
  }, [currentModuleDetail, moduleLinkDetail]);

  if (!isLoading && !lessonLinkDetail) {
    return <div>No lesson found</div>;
  }

  if (!isLoadingModuleLink && !moduleLinkDetail) {
    return <div>No module found</div>;
  }

  if (isLoading || isLoadingModuleLink) return <Spinner className="mx-auto" />;
  if (isError || isErrorModuleLink)
    return (
      <ErrorDisplay error={lessonError || moduleError} messageSource="api" />
    );

  const pageIndex = lessonLinkDetail.pages.findIndex(
    (page) => page._id === page_id,
  );

  const confirmationBoxMessage = (
    <p>
      Your progress {lesson ? "in" : ""} {lesson ? <b>{lesson.title}</b> : ""}{" "}
      will be saved.
    </p>
  );

  return (
    <>
      {showComponent ? (
        <div className={`shadow-lg ${className ? className : ""}`}>
          <CustomButton
            className="w-full"
            color={"#FF0000"}
            icon={
              <img
                className="w-12 h-12"
                src={lessonLinkDetail.icon.src}
                alt={lessonLinkDetail.icon.alt}
              />
            }
            onClick={() => setOpenConfirmation(true)}
            text={label ? label : lessonLinkDetail.title}
            disabled={disabled}
          />
          <ConfirmationBox
            isOpen={openConfirmation}
            message={confirmationBoxMessage}
            title={`Do you want to open ${label ? label : lessonLinkDetail.title.replace(/[.!?,;:]+$/, "")}?`}
            onConfirm={() => {
              // Saving the Inputs of Page we are moving "from"
              if (savePageInput) {
                savePageInput();
              }
              // Saving the Inputs of Page we are moving "to"
              mutation.mutate({ lesson: lesson_id, module: module_id });
              navigate(
                `/topics/${moduleLinkDetail?.slug ?? module_id}/sections/${lessonLinkDetail?.slug ?? lesson_id}`,
                {
                  state: {
                    referringPageName: lesson?.title || lessonLinkDetail.title,
                    initialPage: pageIndex === -1 ? 0 : pageIndex,
                  },
                },
              );
            }}
            onCancel={() => setOpenConfirmation(false)}
            confirmText="Open"
            cancelText="Stay Here"
          />
        </div>
      ) : (
        <></>
      )}
    </>
  );
}
